import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Chip, CircularProgress, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Alert, Stack, MenuItem, LinearProgress
} from '@mui/material';
import { Security as AdminIcon, Add as AddIcon } from '@mui/icons-material';
import axios from 'axios';
import apiClient from '../api/client';
import ControlPlanePanels from './ControlPlanePanels';

type Tenant = {
  organizationId: string; name: string; ownerEmail: string; planId: string | null;
  isSuspended: boolean; subscriptionStatus: string | null; syncStatus: string;
};
type Plan = { id: string; name: string };
type Mutation = { status: 'SUCCESS' | 'RECOVERY_REQUIRED'; organizationId: string; ownerSetupLink?: string };
const emptyForm = { name: '', ownerName: '', ownerEmail: '', planId: '', industry: '' };
const errorMessage = (error: unknown) => axios.isAxiosError(error)
  ? `${error.response?.data?.message || 'Request failed.'}${error.response?.data?.correlationId ? ` Reference: ${error.response.data.correlationId}` : ''}`
  : 'Request failed. Please try again.';

const Organizations: React.FC = () => {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ text: string; warning: boolean } | null>(null);
  const [setup, setSetup] = useState<{ email: string; url: string } | null>(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const refresh = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get<Tenant[]>('/api/v1/super-admin/tenants');
      setTenants(response.data);
    } catch (e) {
      setError(`Unable to refresh organizations. ${errorMessage(e)}`);
    } finally { setLoading(false); }
  };
  useEffect(() => {
    void refresh();
    apiClient.get<Plan[]>('/api/v1/super-admin/plans').then(r => setPlans(r.data))
      .catch(e => setError(`Unable to load provisioning plans. ${errorMessage(e)}`));
  }, []);

  const mutate = async (path: string, payload?: typeof emptyForm, ownerEmail?: string) => {
    setBusy(true); setError(null); setNotice(null); setSetup(null);
    try {
      const response = await apiClient.post<Mutation>(path, payload, { headers: { 'X-Request-ID': crypto.randomUUID() } });
      const recovery = response.data.status === 'RECOVERY_REQUIRED';
      setNotice({ warning: recovery, text: recovery
        ? `Organization ${response.data.organizationId} is saved. Account or Firestore synchronization needs recovery. Use Reconcile; do not provision it again.`
        : `Organization ${response.data.organizationId} updated successfully.` });
      if (response.data.ownerSetupLink) setSetup({ email: ownerEmail || '', url: response.data.ownerSetupLink });
      if (payload) { setOpen(false); setForm(emptyForm); }
      await refresh();
    } catch (e) { setError(errorMessage(e)); }
    finally { setBusy(false); }
  };
  const valid = Object.values(form).every(v => v.trim()) && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.ownerEmail);

  return <Box sx={{ p: 3 }}>
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
      <Stack direction="row" spacing={2} alignItems="center"><AdminIcon color="primary" fontSize="large" />
        <Typography variant="h4">Organization Control Plane</Typography></Stack>
      <Stack direction="row" spacing={1}>
        <Button onClick={() => { setError(null); void refresh(); }} disabled={busy || loading}>Refresh</Button>
        <Button variant="contained" startIcon={<AddIcon />} disabled={busy || !plans.length}
          onClick={() => { setError(null); setOpen(true); }}>Provision New Tenant</Button>
      </Stack>
    </Stack>
    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
    {notice && <Alert severity={notice.warning ? 'warning' : 'success'} sx={{ mb: 2 }}>{notice.text}</Alert>}
    {setup && <Alert severity="info" sx={{ mb: 2 }} onClose={() => setSetup(null)}>
      Owner account is ready for {setup.email}. Copy this private password-setup link and share it securely with the owner.
      <TextField value={setup.url} fullWidth size="small" inputProps={{ readOnly: true }} sx={{ mt: 1 }} />
      <Button onClick={() => navigator.clipboard.writeText(setup.url).catch(() => setError('Copy failed. Select and copy the link manually.'))}>Copy setup link</Button>
    </Alert>}
    {loading && <LinearProgress />}
    <Stack direction="row" spacing={2} sx={{ mb:2 }}><TextField label="Search organizations" value={search} onChange={e=>setSearch(e.target.value)} /><TextField select label="Status" value={statusFilter} onChange={e=>setStatusFilter(e.target.value)} sx={{minWidth:180}}>{['ALL','TRIAL','ACTIVE','GRACE','SUSPENDED','EXPIRED'].map(s=><MenuItem key={s} value={s}>{s}</MenuItem>)}</TextField></Stack>
    <TableContainer component={Paper}><Table>
      <TableHead><TableRow><TableCell>Organization</TableCell><TableCell>Plan</TableCell>
        <TableCell>Subscription</TableCell><TableCell>Synchronization</TableCell><TableCell align="right">Actions</TableCell>
      </TableRow></TableHead>
      <TableBody>
        {!loading && !tenants.length && <TableRow><TableCell colSpan={5}>No organizations found.</TableCell></TableRow>}
        {tenants.filter(t => `${t.name} ${t.ownerEmail} ${t.organizationId}`.toLowerCase().includes(search.toLowerCase()) && (statusFilter === 'ALL' || (t.isSuspended ? 'SUSPENDED' : t.subscriptionStatus) === statusFilter)).map(tenant => <TableRow key={tenant.organizationId}>
          <TableCell><Typography fontWeight="bold">{tenant.name}</Typography>
            <Typography variant="body2">{tenant.ownerEmail}</Typography>
            <Typography variant="caption">{tenant.organizationId}</Typography></TableCell>
          <TableCell>{tenant.planId || 'Unassigned'}</TableCell>
          <TableCell><Chip size="small" label={tenant.isSuspended ? 'SUSPENDED' : tenant.subscriptionStatus || 'UNKNOWN'}
            color={tenant.isSuspended || tenant.subscriptionStatus === 'EXPIRED' ? 'error' : 'default'} /></TableCell>
          <TableCell><Chip size="small" label={tenant.syncStatus}
            color={tenant.syncStatus === 'TENANT_SYNCED' ? 'success' : 'warning'} /></TableCell>
          <TableCell align="right"><Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button disabled={busy} onClick={() => void mutate(`/api/v1/super-admin/tenants/${encodeURIComponent(tenant.organizationId)}/reconcile`, undefined, tenant.ownerEmail)}>Reconcile</Button>
            <Button variant="outlined" disabled={busy} color={tenant.isSuspended ? 'success' : 'error'}
              onClick={() => { if (window.confirm(`${tenant.isSuspended ? 'Reactivate' : 'Suspend'} ${tenant.name}?`)) void mutate(`/api/v1/super-admin/tenants/${encodeURIComponent(tenant.organizationId)}/suspend?suspend=${!tenant.isSuspended}`, undefined, tenant.ownerEmail); }}>
              {tenant.isSuspended ? 'Reactivate' : 'Suspend'}</Button>
          </Stack></TableCell>
        </TableRow>)}
      </TableBody>
    </Table></TableContainer>
    <Dialog open={open} onClose={() => { if (!busy) setOpen(false); }} fullWidth maxWidth="sm">
      <DialogTitle>Provision New Tenant</DialogTitle>
      <DialogContent><Stack spacing={2} sx={{ mt: 1 }}>
        {error && <Alert severity="error">{error}</Alert>}
        <Alert severity="info">Use a new owner email. Existing identities cannot be reassigned to another company. A private password-setup link will be provided after provisioning.</Alert>
        {(['name', 'ownerName', 'ownerEmail', 'industry'] as const).map(key => <TextField key={key} required fullWidth
          label={{ name: 'Company name', ownerName: 'Owner name', ownerEmail: 'Owner email', industry: 'Industry' }[key]}
          type={key === 'ownerEmail' ? 'email' : 'text'} value={form[key]} disabled={busy}
          inputProps={{ maxLength: key === 'name' ? 235 : 255 }}
          onChange={event => setForm({ ...form, [key]: event.target.value })} />)}
        <TextField select label="Initial trial plan" required value={form.planId} disabled={busy}
          onChange={event => setForm({ ...form, planId: event.target.value })}>
          {plans.map(plan => <MenuItem key={plan.id} value={plan.id}>{plan.name}</MenuItem>)}
        </TextField>
      </Stack></DialogContent>
      <DialogActions><Button disabled={busy} onClick={() => setOpen(false)}>Cancel</Button>
        <Button variant="contained" disabled={busy || !valid} onClick={() => void mutate('/api/v1/super-admin/tenants', form, form.ownerEmail)}>
          {busy ? <CircularProgress size={24} /> : 'Provision tenant'}</Button></DialogActions>
    </Dialog>
  </Box>;
};
export default function SuperAdmin() { return <ControlPlanePanels organizations={<Organizations />} />; }


